import React from 'react';
import { 
  Sparkles, 
  Castle, 
  Hotel, 
  Ticket, 
  Wand2, 
  Utensils, 
  Plane, 
  Car, 
  Ship, 
  MapPin,
  Calendar,
  Users,
  Clock,
  DollarSign,
  CheckCircle2
} from 'lucide-react';

const MagicalTemplate = ({ quote, onPayment, isAgentPreview = false }) => {
  if (!quote) {
    return null;
  }

  // Helper function to format currency
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Helper function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    // Parse date in local timezone to avoid UTC offset issues
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Helper function to format time
  const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      const date = new Date(`2000-01-01T${timeString}`);
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return timeString;
    }
  };

  // Get client last name for hero section
  const getClientLastName = () => {
    if (quote.client?.lastName) return quote.client.lastName;
    if (quote.client?.firstName) return quote.client.firstName;
    return 'Your';
  };

  // Get icon for component type
  const getComponentIcon = (component) => {
    const type = component.type?.toLowerCase() || '';
    const title = component.title?.toLowerCase() || '';
    
    // Hotel/Resort
    if (type.includes('hotel') || title.includes('hotel') || title.includes('resort')) {
      return <Castle className="w-8 h-8 text-purple-600" />;
    }
    
    // Theme Parks & Activities
    if (type.includes('disney') || type.includes('universal') || type.includes('theme') || 
        type.includes('activity') || title.includes('park') || title.includes('ticket')) {
      return <Ticket className="w-8 h-8 text-purple-600" />;
    }
    
    // Dining/Restaurant
    if (type.includes('restaurant') || type.includes('dining') || title.includes('dining') || title.includes('restaurant')) {
      return <Utensils className="w-8 h-8 text-purple-600" />;
    }
    
    // Flight
    if (type.includes('flight') || title.includes('flight')) {
      return <Plane className="w-8 h-8 text-purple-600" />;
    }
    
    // Car Rental
    if (type.includes('car') || title.includes('car') || title.includes('rental')) {
      return <Car className="w-8 h-8 text-purple-600" />;
    }
    
    // Cruise
    if (type.includes('cruise') || title.includes('cruise')) {
      return <Ship className="w-8 h-8 text-purple-600" />;
    }
    
    // Default magical icon
    return <Wand2 className="w-8 h-8 text-purple-600" />;
  };

  // Check if component is a theme park/activity for special styling
  const isThemeParkComponent = (component) => {
    const type = component.type?.toLowerCase() || '';
    const title = component.title?.toLowerCase() || '';
    return type.includes('disney') || type.includes('universal') || type.includes('theme') || 
           type.includes('activity') || title.includes('park') || title.includes('ticket');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 overflow-x-hidden">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        
        {/* Hero Section */}
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl shadow-purple-900/5 p-6 sm:p-10 mb-8 sm:mb-12 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Sparkles className="w-10 h-10 text-purple-600 animate-pulse" />
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-purple-900">
              The {getClientLastName()} Family's Magical Adventure!
            </h1>
            <Sparkles className="w-10 h-10 text-purple-600 animate-pulse" />
          </div>
          
          {quote.quoteTitle && (
            <p className="text-lg sm:text-xl text-purple-700 font-semibold mb-6">
              {quote.quoteTitle}
            </p>
          )}

          {/* Trip Overview */}
          <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mt-6">
            {quote.travelDates?.start && (
              <div className="flex items-center justify-center gap-3 bg-purple-50 p-4 rounded-2xl">
                <Calendar className="w-6 h-6 text-purple-600" />
                <div className="text-left">
                  <div className="text-xs text-purple-600 font-semibold">Travel Dates</div>
                  <div className="text-sm text-purple-900 font-bold">{formatDate(quote.travelDates.start)}</div>
                  {quote.travelDates.end && (
                    <div className="text-xs text-purple-700">to {formatDate(quote.travelDates.end)}</div>
                  )}
                </div>
              </div>
            )}
            
            {(quote.travelers?.adults || quote.travelers?.children?.length) && (
              <div className="flex items-center justify-center gap-3 bg-purple-50 p-4 rounded-2xl">
                <Users className="w-6 h-6 text-purple-600" />
                <div className="text-left">
                  <div className="text-xs text-purple-600 font-semibold">Travelers</div>
                  <div className="text-sm text-purple-900 font-bold">
                    {quote.travelers.adults || 0} Adult{quote.travelers.adults !== 1 ? 's' : ''}
                    {quote.travelers.children?.length > 0 && `, ${quote.travelers.children.length} Child${quote.travelers.children.length !== 1 ? 'ren' : ''}`}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Itinerary Components */}
        <div className="space-y-6">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-purple-900 text-center mb-6">
            ✨ Your Magical Itinerary ✨
          </h2>

          {quote.components && quote.components.length > 0 ? (
            <div className="space-y-4">
              {quote.components.map((component, index) => {
                const isThemePark = isThemeParkComponent(component);
                
                return (
                  <div 
                    key={component.id || index} 
                    className={`bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl shadow-purple-900/5 overflow-hidden ${
                      isThemePark ? 'border-2 border-purple-200 ring-2 ring-purple-100' : ''
                    }`}
                  >
                    {/* Component Header */}
                    <div className="bg-gradient-to-r from-purple-100 to-pink-100 p-4 sm:p-6">
                      {/* Mobile: Stacked Layout */}
                      <div className="block sm:hidden">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="flex-shrink-0 bg-white p-2 rounded-2xl shadow-md">
                              {getComponentIcon(component)}
                            </div>
                            <div className="text-2xl font-extrabold text-purple-900">
                              {formatCurrency(component.price)}
                            </div>
                          </div>
                        </div>
                        <h3 className="text-lg font-extrabold text-purple-900 mb-2">
                          {component.title || `Experience ${index + 1}`}
                        </h3>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="inline-block bg-purple-600 text-white px-2.5 py-1 rounded-full text-xs font-semibold">
                            {component.type || 'Experience'}
                          </span>
                          {isThemePark && (
                            <span className="inline-block bg-gradient-to-r from-purple-600 to-pink-600 text-white px-2.5 py-1 rounded-full text-xs font-semibold animate-pulse">
                              ⭐ Main Event
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Desktop: Horizontal Layout */}
                      <div className="hidden sm:flex items-start gap-4">
                        <div className="flex-shrink-0 bg-white p-3 rounded-2xl shadow-md">
                          {getComponentIcon(component)}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <h3 className="text-xl sm:text-2xl font-extrabold text-purple-900 mb-2 break-words">
                            {component.title || `Experience ${index + 1}`}
                          </h3>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="inline-block bg-purple-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                              {component.type || 'Experience'}
                            </span>
                            {isThemePark && (
                              <span className="inline-block bg-gradient-to-r from-purple-600 to-pink-600 text-white px-3 py-1 rounded-full text-sm font-semibold animate-pulse">
                                ⭐ Main Event
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="text-right flex-shrink-0">
                          <div className="text-2xl sm:text-3xl font-extrabold text-purple-900">
                            {formatCurrency(component.price)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Component Details */}
                    {component.data && Object.keys(component.data).length > 0 && (
                      <div className="p-4 sm:p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          
                          {/* Hotel/Resort specific fields */}
                          {(component.type?.toLowerCase().includes('hotel') || component.title?.toLowerCase().includes('hotel') || component.title?.toLowerCase().includes('resort')) && (
                            <>
                              {component.data.property_name && (
                                <div className="flex items-start gap-3 bg-purple-50 p-4 rounded-2xl">
                                  <Hotel className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <div className="text-sm font-semibold text-purple-900">Property</div>
                                    <div className="text-purple-800">{component.data.property_name}</div>
                                  </div>
                                </div>
                              )}
                              
                              {component.data.check_in_date && (
                                <div className="flex items-start gap-3 bg-purple-50 p-4 rounded-2xl">
                                  <Calendar className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <div className="text-sm font-semibold text-purple-900">Check-In</div>
                                    <div className="text-purple-800 font-bold">{formatDate(component.data.check_in_date)}</div>
                                  </div>
                                </div>
                              )}
                              
                              {component.data.check_out_date && (
                                <div className="flex items-start gap-3 bg-purple-50 p-4 rounded-2xl">
                                  <Calendar className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <div className="text-sm font-semibold text-purple-900">Check-Out</div>
                                    <div className="text-purple-800 font-bold">{formatDate(component.data.check_out_date)}</div>
                                  </div>
                                </div>
                              )}
                              
                              {component.data.room_type && (
                                <div className="flex items-start gap-3 bg-purple-50 p-4 rounded-2xl">
                                  <Castle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <div className="text-sm font-semibold text-purple-900">Room Type</div>
                                    <div className="text-purple-800">{component.data.room_type}</div>
                                  </div>
                                </div>
                              )}
                            </>
                          )}

                          {/* Restaurant/Dining specific fields */}
                          {(component.type?.toLowerCase().includes('restaurant') || component.type?.toLowerCase().includes('dining') || component.title?.toLowerCase().includes('dining') || component.title?.toLowerCase().includes('restaurant')) && (
                            <>
                              {component.data.reservation_time && (
                                <div className="flex items-start gap-3 bg-pink-50 p-4 rounded-2xl border-2 border-pink-200">
                                  <Clock className="w-5 h-5 text-pink-600 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <div className="text-sm font-semibold text-pink-900">Reservation Time</div>
                                    <div className="text-pink-800 font-bold text-lg">{formatTime(component.data.reservation_time)}</div>
                                  </div>
                                </div>
                              )}
                              
                              {component.data.reservation_date && (
                                <div className="flex items-start gap-3 bg-pink-50 p-4 rounded-2xl">
                                  <Calendar className="w-5 h-5 text-pink-600 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <div className="text-sm font-semibold text-pink-900">Date</div>
                                    <div className="text-pink-800">{formatDate(component.data.reservation_date)}</div>
                                  </div>
                                </div>
                              )}
                              
                              {component.data.location && (
                                <div className="flex items-start gap-3 bg-pink-50 p-4 rounded-2xl">
                                  <MapPin className="w-5 h-5 text-pink-600 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <div className="text-sm font-semibold text-pink-900">Location</div>
                                    <div className="text-pink-800">{component.data.location}</div>
                                  </div>
                                </div>
                              )}
                            </>
                          )}

                          {/* Generic data fields for other components */}
                          {Object.entries(component.data).map(([key, value]) => {
                            // Skip fields we've already handled
                            if (key === 'property_name' || key === 'check_in_date' || key === 'check_out_date' || 
                                key === 'room_type' || key === 'reservation_time' || key === 'reservation_date' || 
                                key === 'location' || key === 'isOption' || key === 'optionDescription' || !value) {
                              return null;
                            }
                            
                            // Check if this is a price field
                            const isPriceField = key.toLowerCase().includes('price') || 
                                                 key.toLowerCase().includes('cost') || 
                                                 key.toLowerCase().includes('fee') ||
                                                 key.toLowerCase().includes('total') ||
                                                 key.toLowerCase().includes('deposit');
                            const isNumeric = !isNaN(parseFloat(value)) && isFinite(value);
                            
                            let displayValue = String(value);
                            if (isPriceField && isNumeric) {
                              displayValue = formatCurrency(parseFloat(value));
                            }
                            
                            return (
                              <div key={key} className="flex items-start gap-3 bg-indigo-50 p-4 rounded-2xl">
                                <Sparkles className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                                <div>
                                  <div className="text-sm font-semibold text-indigo-900 capitalize">
                                    {key.replace(/_/g, ' ')}
                                  </div>
                                  <div className="text-indigo-800">{displayValue}</div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Component Photos */}
                    {component.photos && component.photos.length > 0 && (
                      <div className="mt-6">
                        <h4 className="text-lg font-bold text-purple-900 mb-3 flex items-center gap-2">
                          <span>📸</span> Photos
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          {component.photos.map((photo, photoIndex) => (
                            <div 
                              key={photoIndex} 
                              className="relative group cursor-pointer rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow bg-white"
                              onClick={() => window.open(photo.url, '_blank')}
                            >
                              <img 
                                src={photo.url} 
                                alt={photo.name || 'Component photo'} 
                                className="w-full h-auto object-contain"
                                style={{ maxHeight: '300px' }}
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-purple-900/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                                <span className="text-white text-sm font-semibold">{photo.name || 'View Photo'}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Component Documents */}
                    {component.documents && component.documents.length > 0 && (
                      <div className="mt-6">
                        <h4 className="text-lg font-bold text-purple-900 mb-3 flex items-center gap-2">
                          <span>📋</span> Documents
                        </h4>
                        <div className="space-y-2">
                          {component.documents.map((doc, docIndex) => (
                            <a 
                              key={docIndex}
                              href={doc.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center gap-3 bg-purple-50 hover:bg-purple-100 px-4 py-3 rounded-xl transition-colors border border-purple-200"
                            >
                              <span className="text-2xl">📄</span>
                              <span className="text-purple-900 font-medium">{doc.name}</span>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl shadow-purple-900/5 p-12 text-center">
              <Wand2 className="w-16 h-16 text-purple-300 mx-auto mb-4" />
              <p className="text-purple-600 text-lg">Your magical itinerary is being prepared...</p>
            </div>
          )}
        </div>

        {/* Notes Section */}
        {quote.notes && (
          <div className="mt-8 bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl shadow-purple-900/5 p-6 sm:p-8">
            <h3 className="text-2xl font-extrabold text-purple-900 mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              Special Notes
            </h3>
            <p className="text-purple-800 whitespace-pre-wrap leading-relaxed">
              {quote.notes}
            </p>
          </div>
        )}

        {/* Pricing Footer - Grand Reveal */}
        <div className="mt-8 sm:mt-12 bg-gradient-to-r from-purple-600 to-pink-600 rounded-3xl shadow-2xl p-6 sm:p-12 text-center text-white">
          <div className="mb-6">
            <Sparkles className="w-12 h-12 mx-auto mb-4 animate-pulse" />
            <h2 className="text-3xl sm:text-4xl font-extrabold mb-2">
              Your Magical Adventure Awaits!
            </h2>
            <p className="text-purple-100 text-lg">
              Everything you need for an unforgettable journey
            </p>
          </div>

          <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 sm:p-8 mb-6 sm:mb-8">
            <div className="text-sm font-semibold text-purple-100 mb-2">Total Investment</div>
            <div className="text-5xl sm:text-6xl md:text-7xl font-black mb-2">
              {formatCurrency(quote.totalPrice)}
            </div>
            
            {quote.depositAmount > 0 && (
              <div className="border-t border-white/20 pt-4 mt-4">
                <div className="flex items-center justify-center gap-2 text-purple-100 mb-2">
                  <DollarSign className="w-5 h-5" />
                  <span className="font-semibold">Initial Deposit Due</span>
                </div>
                <div className="text-3xl font-extrabold">
                  {formatCurrency(quote.depositAmount)}
                </div>
                {quote.depositDueDate && (
                  <div className="text-sm text-purple-100 mt-2">
                    Due by {formatDate(quote.depositDueDate)}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Payment Button */}
          {onPayment && (
            <button
              onClick={onPayment}
              disabled={isAgentPreview}
              className={`w-full bg-white text-purple-600 font-extrabold py-4 sm:py-5 px-6 sm:px-8 rounded-2xl text-lg sm:text-xl transition-all duration-300 flex items-center justify-center gap-3 ${
                isAgentPreview ? 'opacity-50 cursor-not-allowed' : 'hover:bg-purple-50 hover:scale-105 shadow-2xl'
              }`}
            >
              <CheckCircle2 className="w-6 h-6" />
              {isAgentPreview ? 'Preview Mode - Payment Disabled' : 'Approve Your Magic'}
              <Sparkles className="w-6 h-6" />
            </button>
          )}

          {!isAgentPreview && (
            <p className="text-purple-100 text-sm mt-4">
              ✨ Let the magic begin! Your dream vacation is just one click away ✨
            </p>
          )}
        </div>

        {/* Agent Contact Footer */}
        {(quote.agent_name || quote.agent_email || quote.agent_phone) && (
          <div className="mt-12 bg-gradient-to-r from-purple-100 via-pink-100 to-purple-100 rounded-3xl p-8 border-2 border-purple-200">
            <div className="text-center mb-6">
              <Sparkles className="w-8 h-8 text-purple-600 mx-auto mb-3" />
              <h3 className="text-2xl font-bold text-purple-900 mb-2">Your Travel Advisor</h3>
              <div className="w-16 h-1 bg-gradient-to-r from-purple-600 to-pink-600 mx-auto rounded-full"></div>
            </div>
            
            <div className="max-w-md mx-auto">
              {quote.agent_name && (
                <div className="text-center mb-4">
                  <p className="text-xl font-bold text-purple-900">{quote.agent_name}</p>
                  {quote.agent_business_name && (
                    <p className="text-purple-700 font-semibold">{quote.agent_business_name}</p>
                  )}
                </div>
              )}
              
              <div className="space-y-3">
                {quote.agent_email && (
                  <a 
                    href={`mailto:${quote.agent_email}`}
                    className="flex items-center justify-center gap-3 bg-white/80 backdrop-blur-sm px-4 py-3 rounded-2xl hover:bg-white transition-all shadow-sm hover:shadow-md"
                  >
                    <span className="text-2xl">📧</span>
                    <span className="text-purple-900 font-medium">{quote.agent_email}</span>
                  </a>
                )}
                
                {quote.agent_phone && (
                  <a 
                    href={`tel:${quote.agent_phone}`}
                    className="flex items-center justify-center gap-3 bg-white/80 backdrop-blur-sm px-4 py-3 rounded-2xl hover:bg-white transition-all shadow-sm hover:shadow-md"
                  >
                    <span className="text-2xl">📞</span>
                    <span className="text-purple-900 font-medium">{quote.agent_phone}</span>
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer Tagline */}
        <div className="mt-8 text-center">
          <p className="text-purple-600 text-sm flex items-center justify-center gap-2">
            <Sparkles className="w-4 h-4" />
            Where Dreams Come True
            <Sparkles className="w-4 h-4" />
          </p>
        </div>
      </div>
    </div>
  );
};

export default MagicalTemplate;
